import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { Loader2, Download, Mail, CalendarClock, Search, X, ChevronDown } from "lucide-react";
import { SendEmailReportModal } from "../components/SendEmailReportModal";
import { ScheduleReportModal } from "../components/ScheduleReportModal";
import { useAuthStore } from "../stores/authStore";
import { ACTION_KEYS } from "../constants/actions";
import { format, subDays } from "date-fns";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx-js-style";
import { resolveColorHex } from "../utils/teamColors";

const DEFAULT_TIMEZONE = "America/Los_Angeles";

function formatInPacific(dateString: string, formatStr: string = "MMM d, yyyy"): string {
  const utcDate = new Date(dateString);
  const pacificDate = utcToZonedTime(utcDate, DEFAULT_TIMEZONE);
  return format(pacificDate, formatStr);
}

interface PCCAgentMetrics {
  inboundCalls: number;
  outboundCalls: number;
  totalHolds: number;
  avgHoldMinutes: number;
  /** Total in-call time (min) / scheduled hours. Null when no team member schedule matched. */
  attMinutes: number | null;
  ahtMinutes: number;
  /** Inbound-answered only — excludes outbound-connected calls. */
  answeredCalls: number;
  /** Outbound-connected calls — combine with answeredCalls for Hold/AHT's "(In & out)" denominator. */
  outboundAnsweredCalls: number;
  totalTalkMinutes: number;
  scheduledHours: number | null;
  avgAsaSeconds: number;
  avgAcwSeconds: number;
}

interface PCCAgentRow {
  agentId: string | null;
  agentName: string | null;
  position: string | null;
  teamName: string | null;
  teamColor: string | null;
  regionName: string | null;
  metrics: PCCAgentMetrics;
}

interface DateRange {
  start: string;
  end: string;
}

const ROLE_LABELS: Record<string, string> = {
  pcc_incoming:        "PCC Incoming",
  pcc_outbound:        "PCC Outbound",
  onboarding:          "Onboarding",
  team_lead:           "Team Lead",
  operations_manager:  "Operations Manager",
  kpi:                 "KPI",
  upper_management:    "Upper Management",
  qa:                  "QA",
  outbound_mod:        "Outbound Mod",
  sales_marketing:     "Sales & Marketing",
  tech_dev_support:    "Tech Dev Support",
  hr:                  "HR",
  accounting:          "Accounting",
};

const fmtRole = (role: string | null): string =>
  role ? (ROLE_LABELS[role] ?? role) : "—";

const fmt = (value: number) => value.toFixed(2);

const fmtAsa = (seconds: number): string => {
  if (seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const fmtAcw = fmtAsa; // same formatting: "45s" or "1m 23s" or "—"

export function PCCPerformance() {
  const canSendEmail = useAuthStore((s) => s.hasPermission(ACTION_KEYS.EMAIL_REPORT_SEND));

  const [agents, setAgents] = useState<PCCAgentRow[]>([]);
  const [totals, setTotals] = useState<PCCAgentMetrics | null>(null);
  // Voicemails are always inbound and never carry an agent identity (Aircall only
  // attributes a call to an agent once someone engages with it) — page-wide only,
  // can't be a per-agent column.
  const [totalVoicemails, setTotalVoicemails] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [queriedDateRange, setQueriedDateRange] = useState<DateRange | null>(null);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const [startDate, setStartDate] = useState(format(subDays(new Date(), 6), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");
  const [minDurationSeconds, setMinDurationSeconds] = useState<number>(0);
  const [queueFilter, setQueueFilter] = useState("");

  const filteredAgents = useMemo(() => {
    if (!queueFilter.trim()) return agents;
    const search = queueFilter.toLowerCase();
    return agents.filter((a) => a.agentName?.toLowerCase().includes(search));
  }, [agents, queueFilter]);

  type PCCSortCol = "agent" | "inbound" | "outbound" | "holds" | "avgHold" | "avgAcw" | "totalTalk" | "schedHrs" | "att" | "aht" | "asa";
  const [pccSortCol, setPccSortCol] = useState<PCCSortCol | null>(null);
  const [pccSortDir, setPccSortDir] = useState<"asc" | "desc">("desc");
  const handlePCCSort = (col: PCCSortCol) => {
    if (pccSortCol === col) setPccSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setPccSortCol(col); setPccSortDir("desc"); }
  };

  const sortedFilteredAgents = useMemo(() => {
    if (!pccSortCol) return filteredAgents;
    return [...filteredAgents].sort((a, b) => {
      let av: number | string = 0, bv: number | string = 0;
      switch (pccSortCol) {
        case "agent":     av = a.agentName || ""; bv = b.agentName || ""; break;
        case "inbound":   av = a.metrics.inboundCalls;      bv = b.metrics.inboundCalls;      break;
        case "outbound":  av = a.metrics.outboundCalls;     bv = b.metrics.outboundCalls;     break;
        case "holds":     av = a.metrics.totalHolds;        bv = b.metrics.totalHolds;        break;
        case "avgHold":   av = a.metrics.avgHoldMinutes;    bv = b.metrics.avgHoldMinutes;    break;
        case "avgAcw":    av = a.metrics.avgAcwSeconds;     bv = b.metrics.avgAcwSeconds;     break;
        case "totalTalk": av = a.metrics.totalTalkMinutes;  bv = b.metrics.totalTalkMinutes;  break;
        case "schedHrs":  av = a.metrics.scheduledHours ?? -1; bv = b.metrics.scheduledHours ?? -1; break;
        case "att":       av = a.metrics.attMinutes ?? -1;  bv = b.metrics.attMinutes ?? -1;  break;
        case "aht":       av = a.metrics.ahtMinutes;        bv = b.metrics.ahtMinutes;        break;
        case "asa":       av = a.metrics.avgAsaSeconds;     bv = b.metrics.avgAsaSeconds;     break;
      }
      if (typeof av === "string") return pccSortDir === "asc" ? av.localeCompare(bv as string) : (bv as string).localeCompare(av);
      return pccSortDir === "asc" ? (av - (bv as number)) : ((bv as number) - av);
    });
  }, [filteredAgents, pccSortCol, pccSortDir]);

  const downloadExcel = () => {
    if (filteredAgents.length === 0) {
      toast.error("No data to download");
      return;
    }

    const fmtAtt = (m: PCCAgentMetrics) =>
      m.attMinutes !== null ? fmt(m.attMinutes) : "N/A";

    // Normalize a color from the DB — may be a name ("black", "pink a") or hex ("#f2b8a0")
    const normalizeHex = (raw: string | null): string => {
      const resolved = resolveColorHex(raw); // always returns "#rrggbb"
      return resolved.replace("#", "").toUpperCase();
    };

    const isDark = (hex: string): boolean => {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.55;
    };

    const COLS = [
      "Agent (PCC)", "Position", "Inbound Calls", "Outbound", "# of Holds",
      "Avg Hold (min)", "Avg ACW", "Total Talk (min)", "Scheduled Hrs",
      "ATT (min/hr)", "AHT (min)", "ASA",
    ];
    const NUM_COLS = COLS.length;

    const buildAgentRow = (agent: PCCAgentRow): any[] => [
      agent.agentName || agent.agentId || "Unknown",
      fmtRole(agent.position),
      agent.metrics.inboundCalls,
      agent.metrics.outboundCalls,
      agent.metrics.totalHolds,
      fmt(agent.metrics.avgHoldMinutes),
      fmtAcw(agent.metrics.avgAcwSeconds),
      fmt(agent.metrics.totalTalkMinutes),
      agent.metrics.scheduledHours !== null ? fmt(agent.metrics.scheduledHours) : "N/A",
      fmtAtt(agent.metrics),
      fmt(agent.metrics.ahtMinutes),
      fmtAsa(agent.metrics.avgAsaSeconds),
    ];

    const buildSubtotalValues = (members: PCCAgentRow[]): any[] => {
      const n = members.length;
      const avg = (fn: (m: PCCAgentMetrics) => number) =>
        n > 0 ? fmt(members.reduce((s, a) => s + fn(a.metrics), 0) / n) : "—";
      const sum = (fn: (m: PCCAgentMetrics) => number) =>
        members.reduce((s, a) => s + fn(a.metrics), 0);
      const avgAcwSec = n > 0 ? members.reduce((s, a) => s + a.metrics.avgAcwSeconds, 0) / n : 0;
      const avgAsaSec = n > 0 ? members.reduce((s, a) => s + a.metrics.avgAsaSeconds, 0) / n : 0;
      const matched = members.filter((a) => a.metrics.scheduledHours !== null);
      const totalSched = matched.reduce((s, a) => s + a.metrics.scheduledHours!, 0);
      const totalTalk  = matched.reduce((s, a) => s + a.metrics.totalTalkMinutes, 0);
      return [
        sum((m) => m.inboundCalls),
        sum((m) => m.outboundCalls),
        sum((m) => m.totalHolds),
        avg((m) => m.avgHoldMinutes),
        fmtAcw(avgAcwSec),
        fmt(sum((m) => m.totalTalkMinutes)),
        totalSched > 0 ? fmt(totalSched) : "N/A",
        totalSched > 0 ? fmt(totalTalk / totalSched) : "N/A",
        avg((m) => m.ahtMinutes),
        fmtAsa(avgAsaSec),
      ];
    };

    // ── Static styles ─────────────────────────────────────────────────────────
    const center = { horizontal: "center" as const, vertical: "center" as const };
    const left   = { horizontal: "left"   as const, vertical: "center" as const };

    const S = {
      title: {
        fill: { fgColor: { rgb: "1E3A5F" }, patternType: "solid" },
        font: { color: { rgb: "FFFFFF" }, bold: true, sz: 14, name: "Calibri" },
        alignment: center,
      },
      regionHeader: {
        fill: { fgColor: { rgb: "1E3A5F" }, patternType: "solid" },
        font: { color: { rgb: "FFFFFF" }, bold: true, sz: 12, name: "Calibri" },
        alignment: left,
        border: {
          top:    { style: "medium", color: { rgb: "FFFFFF" } },
          bottom: { style: "medium", color: { rgb: "FFFFFF" } },
        },
      },
      colHeader: (_teamColorHex: string) => ({
        fill: { fgColor: { rgb: "2D3748" }, patternType: "solid" },
        font: { color: { rgb: "FFFFFF" }, bold: true, sz: 10, name: "Calibri" },
        alignment: center,
        border: {
          top:    { style: "thin", color: { rgb: "FFFFFF" } },
          bottom: { style: "thin", color: { rgb: "FFFFFF" } },
          right:  { style: "thin", color: { rgb: "FFFFFF" } },
        },
      }),
      teamHeader: (teamColorHex: string) => ({
        fill: { fgColor: { rgb: teamColorHex }, patternType: "solid" },
        font: { color: { rgb: isDark(teamColorHex) ? "FFFFFF" : "1A1A1A" }, bold: true, sz: 11, name: "Calibri" },
        alignment: left,
        border: {
          top:    { style: "thin", color: { rgb: "555555" } },
          bottom: { style: "thin", color: { rgb: "555555" } },
          left:   { style: "thin", color: { rgb: "555555" } },
          right:  { style: "thin", color: { rgb: "555555" } },
        },
      }),
      // Alternating row fill with full grid borders
      dataRow: (_teamColorHex: string, odd: boolean) => ({
        fill: { fgColor: { rgb: odd ? "EAF0F8" : "FFFFFF" }, patternType: "solid" },
        font: { color: { rgb: "1A1A1A" }, sz: 10, name: "Calibri" },
        alignment: center,
        border: {
          right:  { style: "thin", color: { rgb: "555555" } },
          bottom: { style: "thin", color: { rgb: "555555" } },
          top:    { style: "thin", color: { rgb: "555555" } },
        },
      }),
      // Col 0: thick left team-color accent + full grid borders
      dataRowName: (teamColorHex: string, odd: boolean) => ({
        fill: { fgColor: { rgb: odd ? "EAF0F8" : "FFFFFF" }, patternType: "solid" },
        font: { color: { rgb: "1A1A1A" }, sz: 10, name: "Calibri" },
        alignment: left,
        border: {
          left:   { style: "thick", color: { rgb: teamColorHex } },
          right:  { style: "thin",  color: { rgb: "555555" } },
          bottom: { style: "thin",  color: { rgb: "555555" } },
          top:    { style: "thin",  color: { rgb: "555555" } },
        },
      }),
      teamSubtotal: (teamColorHex: string) => ({
        fill: { fgColor: { rgb: teamColorHex }, patternType: "solid" },
        font: { color: { rgb: isDark(teamColorHex) ? "FFFFFF" : "1A1A1A" }, bold: true, italic: true, sz: 10, name: "Calibri" },
        alignment: center,
        border: {
          top:   { style: "thin", color: { rgb: "555555" } },
          bottom: { style: "thin", color: { rgb: "555555" } },
          right:  { style: "thin", color: { rgb: "555555" } },
        },
      }),
      teamSubtotalLabel: (teamColorHex: string) => ({
        fill: { fgColor: { rgb: teamColorHex }, patternType: "solid" },
        font: { color: { rgb: isDark(teamColorHex) ? "FFFFFF" : "1A1A1A" }, bold: true, italic: true, sz: 10, name: "Calibri" },
        alignment: left,
        border: {
          top:    { style: "thin", color: { rgb: "555555" } },
          bottom: { style: "thin", color: { rgb: "555555" } },
          left:   { style: "thick", color: { rgb: teamColorHex } },
          right:  { style: "thin", color: { rgb: "555555" } },
        },
      }),
      grandTotal: {
        fill: { fgColor: { rgb: "1E3A5F" }, patternType: "solid" },
        font: { color: { rgb: "FFFFFF" }, bold: true, sz: 11, name: "Calibri" },
        alignment: center,
        border: {
          top:    { style: "medium", color: { rgb: "FFFFFF" } },
          bottom: { style: "medium", color: { rgb: "FFFFFF" } },
        },
      },
      grandTotalLabel: {
        fill: { fgColor: { rgb: "1E3A5F" }, patternType: "solid" },
        font: { color: { rgb: "FFFFFF" }, bold: true, sz: 11, name: "Calibri" },
        alignment: left,
      },
      spacer: { fill: { fgColor: { rgb: "FFFFFF" }, patternType: "solid" } },
      regionSpacer: { fill: { fgColor: { rgb: "F0F2F5" }, patternType: "solid" } },
    };

    // ── Build AOA + row meta ──────────────────────────────────────────────────
    const aoa: any[][] = [];
    const merges: XLSX.Range[] = [];
    type RowType =
      | "title" | "regionHeader"
      | "teamHeader" | "colHeader"
      | "data" | "teamSubtotal"
      | "grandTotal" | "spacer" | "regionSpacer";
    const rowMeta: {
      type: RowType;
      teamColorHex?: string;
      oddRow?: boolean;
    }[] = [];

    const displayRange = queriedDateRange
      ? `${formatInPacific(queriedDateRange.start)} — ${formatInPacific(queriedDateRange.end)}`
      : `${startDate} — ${endDate}`;

    // Title row
    aoa.push([`PCC Performance: ${displayRange}`, ...Array(NUM_COLS - 1).fill("")]);
    merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: NUM_COLS - 1 } });
    rowMeta.push({ type: "title" });

    // ── Group: Region → Team → Agents ─────────────────────────────────────────
    // regionMap: regionName → teamName → agents[]
    const regionMap = new Map<string, Map<string, PCCAgentRow[]>>();
    for (const agent of filteredAgents) {
      const rKey = agent.regionName || "__noregion__";
      const tKey = agent.teamName   || "__unassigned__";
      if (!regionMap.has(rKey)) regionMap.set(rKey, new Map());
      const tMap = regionMap.get(rKey)!;
      if (!tMap.has(tKey)) tMap.set(tKey, []);
      tMap.get(tKey)!.push(agent);
    }

    // Sort regions alphabetically; no-region last
    const sortedRegions = [...regionMap.keys()]
      .filter((k) => k !== "__noregion__")
      .sort((a, b) => a.localeCompare(b));
    if (regionMap.has("__noregion__")) sortedRegions.push("__noregion__");

    for (const regionKey of sortedRegions) {
      const regionLabel = regionKey === "__noregion__" ? "No Region" : regionKey;
      const teamMapForRegion = regionMap.get(regionKey)!;

      // Region header
      aoa.push([`  ${regionLabel.toUpperCase()}`, ...Array(NUM_COLS - 1).fill("")]);
      merges.push({ s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: NUM_COLS - 1 } });
      rowMeta.push({ type: "regionHeader" });

      // Sort teams alphabetically; unassigned last
      const sortedTeams = [...teamMapForRegion.keys()]
        .filter((k) => k !== "__unassigned__")
        .sort((a, b) => a.localeCompare(b));
      if (teamMapForRegion.has("__unassigned__")) sortedTeams.push("__unassigned__");

      for (const teamKey of sortedTeams) {
        const members = teamMapForRegion.get(teamKey)!
          .slice()
          .sort((a, b) => (a.agentName || "").localeCompare(b.agentName || ""));
        const teamLabel = teamKey === "__unassigned__" ? "Unassigned" : teamKey;
        const rawColor = members[0]?.teamColor ?? null;
        const teamColorHex = normalizeHex(rawColor);

        // Team header (spans all cols)
        aoa.push([`  ${teamLabel}`, ...Array(NUM_COLS - 1).fill("")]);
        merges.push({ s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: NUM_COLS - 1 } });
        rowMeta.push({ type: "teamHeader", teamColorHex });

        // Column headers
        aoa.push([...COLS]);
        rowMeta.push({ type: "colHeader", teamColorHex });

        // Agent rows
        members.forEach((agent, i) => {
          aoa.push(buildAgentRow(agent));
          rowMeta.push({ type: "data", teamColorHex, oddRow: i % 2 !== 0 });
        });

        // Team subtotal
        aoa.push([`${teamLabel} — Total`, "", ...buildSubtotalValues(members)]);
        rowMeta.push({ type: "teamSubtotal", teamColorHex });

        // Thin spacer between teams within same region
        aoa.push(Array(NUM_COLS).fill(""));
        rowMeta.push({ type: "spacer" });
      }

      // Thicker spacer between regions
      aoa.push(Array(NUM_COLS).fill(""));
      rowMeta.push({ type: "regionSpacer" });
    }

    // Grand total
    if (totals) {
      aoa.push([
        "GRAND TOTAL",
        "",
        totals.inboundCalls,
        totals.outboundCalls,
        totals.totalHolds,
        fmt(totals.avgHoldMinutes),
        fmtAcw(totals.avgAcwSeconds),
        fmt(totals.totalTalkMinutes),
        totals.scheduledHours !== null ? fmt(totals.scheduledHours) : "N/A",
        fmtAtt(totals),
        fmt(totals.ahtMinutes),
        fmtAsa(totals.avgAsaSeconds),
      ]);
      rowMeta.push({ type: "grandTotal" });
    }

    // ── Build worksheet ───────────────────────────────────────────────────────
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;
    ws["!cols"] = [
      { wch: 26 }, { wch: 20 }, { wch: 13 }, { wch: 16 }, { wch: 11 },
      { wch: 14 }, { wch: 11 }, { wch: 15 }, { wch: 13 },
      { wch: 13 }, { wch: 11 }, { wch: 10 },
    ];
    ws["!rows"] = rowMeta.map((m) => {
      if (m.type === "title")        return { hpt: 28 };
      if (m.type === "regionHeader") return { hpt: 22 };
      if (m.type === "teamHeader")   return { hpt: 20 };
      if (m.type === "colHeader")    return { hpt: 18 };
      if (m.type === "teamSubtotal") return { hpt: 18 };
      if (m.type === "grandTotal")   return { hpt: 22 };
      if (m.type === "regionSpacer") return { hpt: 16 };
      if (m.type === "spacer")       return { hpt: 8 };
      return { hpt: 20 };
    });

    // ── Apply styles ──────────────────────────────────────────────────────────
    for (let r = 0; r < rowMeta.length; r++) {
      const meta = rowMeta[r];
      const hex = meta.teamColorHex ?? "2E5090";

      for (let c = 0; c < NUM_COLS; c++) {
        const ref = XLSX.utils.encode_cell({ r, c });
        if (!ws[ref]) ws[ref] = { v: "", t: "s" };

        switch (meta.type) {
          case "title":
            ws[ref].s = S.title; break;
          case "regionHeader":
            ws[ref].s = S.regionHeader; break;
          case "teamHeader":
            ws[ref].s = S.teamHeader(hex); break;
          case "colHeader":
            ws[ref].s = S.colHeader(hex); break;
          case "data":
            ws[ref].s = c === 0
              ? S.dataRowName(hex, meta.oddRow ?? false)
              : S.dataRow(hex, meta.oddRow ?? false);
            break;
          case "teamSubtotal":
            ws[ref].s = c === 0
              ? S.teamSubtotalLabel(hex)
              : S.teamSubtotal(hex);
            break;
          case "grandTotal":
            ws[ref].s = c === 0 ? S.grandTotalLabel : S.grandTotal; break;
          case "regionSpacer":
            ws[ref].s = S.regionSpacer; break;
          default:
            ws[ref].s = S.spacer;
        }

        // Box border around each team block (left on col 0 for colHeader, right on last col for all)
        const TEAM_BLOCK_TYPES: RowType[] = ["teamHeader", "colHeader", "data", "teamSubtotal"];
        if (TEAM_BLOCK_TYPES.includes(meta.type)) {
          const s = ws[ref].s as any;
          if (c === 0 && meta.type === "colHeader") {
            s.border = { ...s.border, left: { style: "thin", color: { rgb: "FFFFFF" } } };
          }
          if (c === NUM_COLS - 1) {
            s.border = { ...s.border, right: { style: "thin", color: { rgb: "555555" } } };
          }
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PCC Performance");
    XLSX.writeFile(wb, `pcc-performance_${startDate}_to_${endDate}.xlsx`);
    toast.success("Excel file downloaded");
  };

  const [timeseriesLoading, setTimeseriesLoading] = useState(false);
  const [timeseriesInterval, setTimeseriesInterval] = useState<"daily" | "hourly">("daily");
  const [timeseriesDropdownOpen, setTimeseriesDropdownOpen] = useState(false);
  const timeseriesDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (timeseriesDropdownRef.current && !timeseriesDropdownRef.current.contains(e.target as Node)) {
        setTimeseriesDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const downloadTimeseriesExcel = async (interval: "daily" | "hourly") => {
    setTimeseriesLoading(true);
    setTimeseriesDropdownOpen(false);
    try {
      const startDateTime = zonedTimeToUtc(`${startDate}T${startTime}:00`, DEFAULT_TIMEZONE);
      const endDateTime = zonedTimeToUtc(`${endDate}T${endTime}:59`, DEFAULT_TIMEZONE);

      const params = new URLSearchParams();
      params.append("startDate", startDateTime.toISOString());
      params.append("endDate", endDateTime.toISOString());
      params.append("interval", interval);
      params.append("timezone", DEFAULT_TIMEZONE);
      if (minDurationSeconds > 0) params.append("minDurationSeconds", String(minDurationSeconds));

      const response = await fetch(`/api/v1/pcc-performance/timeseries?${params}`);
      if (!response.ok) throw new Error("Failed to fetch timeseries data");
      const json = await response.json();

      const rows: Array<{
        period: string;
        agentName: string;
        teamName: string | null;
        regionName: string | null;
        inboundCalls: number;
        outboundCalls: number;
        answeredCalls: number;
        outboundAnsweredCalls: number;
        totalHolds: number;
        avgHoldMinutes: number;
        totalTalkMinutes: number;
        ahtMinutes: number;
        avgAsaSeconds: number;
      }> = json.data?.rows || [];

      if (rows.length === 0) { toast.error("No data to download"); return; }

      const COLS = [
        interval === "hourly" ? "Date / Hour" : "Date",
        "Agent (PCC)", "Team", "Region",
        "Inbound", "Outbound", "Holds",
        "Avg Hold (min)", "Total Talk (min)", "AHT (min)", "ASA",
      ];

      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Calibri" },
        fill: { fgColor: { rgb: "2D3748" }, patternType: "solid" },
        alignment: { horizontal: "center" as const },
      };
      const periodGroupStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11, name: "Calibri" },
        fill: { fgColor: { rgb: "1E3A5F" }, patternType: "solid" },
        alignment: { horizontal: "left" as const },
      };
      const subtotalStyle = {
        font: { bold: true, sz: 10, name: "Calibri", italic: true },
        fill: { fgColor: { rgb: "E2E8F0" }, patternType: "solid" },
        alignment: { horizontal: "center" as const },
      };
      const subtotalLabelStyle = { ...subtotalStyle, alignment: { horizontal: "left" as const } };
      const dataStyleOdd = {
        font: { sz: 10, name: "Calibri" },
        fill: { fgColor: { rgb: "EAF0F8" }, patternType: "solid" },
        alignment: { horizontal: "center" as const },
      };
      const dataStyleEven = {
        font: { sz: 10, name: "Calibri" },
        fill: { fgColor: { rgb: "FFFFFF" }, patternType: "solid" },
        alignment: { horizontal: "center" as const },
      };
      const titleStyle = {
        font: { bold: true, sz: 14, color: { rgb: "FFFFFF" }, name: "Calibri" },
        fill: { fgColor: { rgb: "1E3A5F" }, patternType: "solid" },
        alignment: { horizontal: "left" as const },
      };

      const fmtAsa = (sec: number) => {
        if (sec <= 0) return "—";
        const m = Math.floor(sec / 60);
        const s = Math.round(sec % 60);
        return m > 0 ? `${m}m ${s}s` : `${s}s`;
      };

      const aoa: any[][] = [];
      const merges: { s: { r: number; c: number }; e: { r: number; c: number } }[] = [];
      type RowMeta = { type: "title" | "colHeader" | "periodHeader" | "data" | "subtotal" | "spacer"; odd?: boolean };
      const rowMeta: RowMeta[] = [];

      // Title
      aoa.push([`PCC Performance — ${interval === "hourly" ? "Hourly" : "Daily"} Breakdown: ${startDate} to ${endDate}`, ...Array(COLS.length - 1).fill("")]);
      merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: COLS.length - 1 } });
      rowMeta.push({ type: "title" });

      // Column headers
      aoa.push([...COLS]);
      rowMeta.push({ type: "colHeader" });

      // Group by period
      const grouped = new Map<string, typeof rows>();
      for (const row of rows) {
        if (!grouped.has(row.period)) grouped.set(row.period, []);
        grouped.get(row.period)!.push(row);
      }

      for (const [period, periodRows] of grouped) {
        // Period header
        aoa.push([period, ...Array(COLS.length - 1).fill("")]);
        merges.push({ s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: COLS.length - 1 } });
        rowMeta.push({ type: "periodHeader" });

        // Agent rows
        periodRows.forEach((r, i) => {
          aoa.push([
            "",
            r.agentName,
            r.teamName || "—",
            r.regionName || "—",
            r.inboundCalls,
            r.outboundCalls,
            r.totalHolds,
            r.avgHoldMinutes.toFixed(2),
            r.totalTalkMinutes.toFixed(2),
            r.ahtMinutes.toFixed(2),
            fmtAsa(r.avgAsaSeconds),
          ]);
          rowMeta.push({ type: "data", odd: i % 2 !== 0 });
        });

        // Subtotal
        // Hold/AHT are "(In & out)" — weight by combined inbound+outbound answered
        // calls, matching the backend's per-row denominator, or the reconstructed
        // total under-weights outbound activity. ASA stays inbound-only, weighted
        // by answeredCalls alone (its own true denominator).
        const tot = periodRows.reduce(
          (acc, r) => {
            const combinedAnswered = r.answeredCalls + r.outboundAnsweredCalls;
            return {
              inbound: acc.inbound + r.inboundCalls,
              outbound: acc.outbound + r.outboundCalls,
              holds: acc.holds + r.totalHolds,
              talk: acc.talk + r.totalTalkMinutes,
              holdTot: acc.holdTot + r.avgHoldMinutes * combinedAnswered,
              handleTot: acc.handleTot + r.ahtMinutes * combinedAnswered,
              combinedAnswered: acc.combinedAnswered + combinedAnswered,
              asaTot: acc.asaTot + r.avgAsaSeconds * r.answeredCalls,
              asaCount: acc.asaCount + r.answeredCalls,
            };
          },
          { inbound: 0, outbound: 0, holds: 0, talk: 0, holdTot: 0, handleTot: 0, combinedAnswered: 0, asaTot: 0, asaCount: 0 }
        );
        aoa.push([
          `${period} — Total`, "", "", "",
          tot.inbound, tot.outbound, tot.holds,
          tot.combinedAnswered > 0 ? (tot.holdTot / tot.combinedAnswered).toFixed(2) : "—",
          tot.talk.toFixed(2),
          tot.combinedAnswered > 0 ? (tot.handleTot / tot.combinedAnswered).toFixed(2) : "—",
          fmtAsa(tot.asaCount > 0 ? tot.asaTot / tot.asaCount : 0),
        ]);
        rowMeta.push({ type: "subtotal" });

        aoa.push(Array(COLS.length).fill(""));
        rowMeta.push({ type: "spacer" });
      }

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!merges"] = merges;
      ws["!freeze"] = { xSplit: 0, ySplit: 2 };
      ws["!cols"] = [
        { wch: 22 }, { wch: 24 }, { wch: 18 }, { wch: 18 },
        { wch: 10 }, { wch: 14 }, { wch: 8 },
        { wch: 14 }, { wch: 16 }, { wch: 10 }, { wch: 10 },
      ];
      ws["!rows"] = rowMeta.map((m) => {
        if (m.type === "title")        return { hpt: 26 };
        if (m.type === "colHeader")    return { hpt: 18 };
        if (m.type === "periodHeader") return { hpt: 20 };
        if (m.type === "subtotal")     return { hpt: 18 };
        if (m.type === "spacer")       return { hpt: 8 };
        return { hpt: 18 };
      });

      for (let r = 0; r < rowMeta.length; r++) {
        const meta = rowMeta[r];
        for (let c = 0; c < COLS.length; c++) {
          const ref = XLSX.utils.encode_cell({ r, c });
          if (!ws[ref]) ws[ref] = { v: "", t: "s" };
          switch (meta.type) {
            case "title":        ws[ref].s = titleStyle; break;
            case "colHeader":    ws[ref].s = headerStyle; break;
            case "periodHeader": ws[ref].s = periodGroupStyle; break;
            case "data":         ws[ref].s = meta.odd ? dataStyleOdd : dataStyleEven; break;
            case "subtotal":     ws[ref].s = c === 0 ? subtotalLabelStyle : subtotalStyle; break;
            default:             ws[ref].s = { fill: { fgColor: { rgb: "FFFFFF" }, patternType: "solid" } };
          }
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, interval === "hourly" ? "Hourly Breakdown" : "Daily Breakdown");
      XLSX.writeFile(wb, `pcc-performance-${interval}_${startDate}_to_${endDate}.xlsx`);
      toast.success(`${interval === "hourly" ? "Hourly" : "Daily"} breakdown downloaded`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    } finally {
      setTimeseriesLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Start date must be before or equal to end date");
      return;
    }

    setLoading(true);
    try {
      const startDateTime = zonedTimeToUtc(`${startDate}T${startTime}:00`, DEFAULT_TIMEZONE);
      const endDateTime = zonedTimeToUtc(`${endDate}T${endTime}:59`, DEFAULT_TIMEZONE);

      const params = new URLSearchParams();
      params.append("startDate", startDateTime.toISOString());
      params.append("endDate", endDateTime.toISOString());
      params.append("limit", "500");
      if (minDurationSeconds > 0) {
        params.append("minDurationSeconds", String(minDurationSeconds));
      }

      const response = await fetch(`/api/v1/pcc-performance/queues?${params}`);
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || "Failed to load PCC performance");
      }

      const data = await response.json();
      setAgents(data.data?.agents || []);
      setTotals(data.data?.totals || null);
      setTotalVoicemails(data.data?.totalVoicemails ?? 0);
      setQueriedDateRange(data.data?.dateRange || null);
    } catch (error) {
      console.error("Failed to load PCC performance", error);
      toast.error(error instanceof Error ? error.message : "Failed to load PCC performance");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, startTime, endTime, minDurationSeconds]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClearFilters = () => {
    setStartDate(format(subDays(new Date(), 6), "yyyy-MM-dd"));
    setEndDate(format(new Date(), "yyyy-MM-dd"));
    setStartTime("00:00");
    setEndTime("23:59");
    setMinDurationSeconds(0);
    setQueueFilter("");
  };

  return (
    <div className="space-y-6">
      {showSendEmailModal && (
        <SendEmailReportModal
          reportType="pcc_performance"
          availableCustomers={[]}
          defaultStartDate={startDate}
          defaultEndDate={endDate}
          minDurationSeconds={minDurationSeconds}
          onClose={() => setShowSendEmailModal(false)}
        />
      )}
      {showScheduleModal && (
        <ScheduleReportModal
          reportType="pcc_performance"
          onClose={() => setShowScheduleModal(false)}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">PCC Performance</h1>
          <p className="mt-1 text-sm text-gray-500">
            View performance metrics for all PCC queues based on call history
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadExcel}
            disabled={loading || filteredAgents.length === 0}
            className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Export Excel
          </button>
          {/* Split button: Daily / Hourly breakdown export */}
          <div ref={timeseriesDropdownRef} className="relative inline-flex">
            <button
              onClick={() => downloadTimeseriesExcel(timeseriesInterval)}
              disabled={timeseriesLoading || loading}
              className="inline-flex items-center gap-2 rounded-l-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
            >
              {timeseriesLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export {timeseriesInterval === "hourly" ? "Hourly" : "Daily"}
            </button>
            <button
              onClick={() => setTimeseriesDropdownOpen((o) => !o)}
              disabled={timeseriesLoading || loading}
              className="inline-flex items-center rounded-r-md border-l border-teal-500 bg-teal-600 px-2 py-2 text-white hover:bg-teal-700 disabled:opacity-50"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            {timeseriesDropdownOpen && (
              <div className="absolute right-0 top-full z-20 mt-1 w-36 rounded-md border border-gray-200 bg-white shadow-lg">
                {(["daily", "hourly"] as const).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => { setTimeseriesInterval(opt); setTimeseriesDropdownOpen(false); downloadTimeseriesExcel(opt); }}
                    className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${timeseriesInterval === opt ? "font-semibold text-teal-700" : "text-gray-700"}`}
                  >
                    {opt === "daily" ? "Daily" : "Hourly"}
                  </button>
                ))}
              </div>
            )}
          </div>
          {canSendEmail && (
            <button
              onClick={() => setShowSendEmailModal(true)}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
            >
              <Mail className="h-4 w-4" />
              Send Email
            </button>
          )}
          {canSendEmail && (
            <button
              onClick={() => setShowScheduleModal(true)}
              className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <CalendarClock className="h-4 w-4" />
              Schedule
            </button>
          )}
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid gap-4 md:grid-cols-7">
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Time</label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              max={format(new Date(), "yyyy-MM-dd")}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">End Time</label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Min Duration (sec)</label>
            <input
              type="number"
              min="0"
              value={minDurationSeconds}
              onChange={(e) => setMinDurationSeconds(Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="0"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">Exclude calls shorter than this</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Filter PCC</label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={queueFilter}
                onChange={(e) => setQueueFilter(e.target.value)}
                placeholder="Search agent..."
                className="w-full rounded-md border border-gray-300 pl-9 pr-8 py-2 text-sm"
              />
              {queueFilter && (
                <button
                  onClick={() => setQueueFilter("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleClearFilters}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Totals Summary */}
      {totals && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Overall Summary</h2>
            {queriedDateRange && (
              <p className="text-sm text-gray-500">
                Data from: {formatInPacific(queriedDateRange.start)} to{" "}
                {formatInPacific(queriedDateRange.end)} (PT)
              </p>
            )}
          </div>
          <div className="grid gap-4 md:grid-cols-10">
            <div className="rounded-lg bg-blue-50 p-3">
              <p className="text-sm text-blue-600">Inbound Calls</p>
              <p className="text-2xl font-bold text-blue-700">
                {totals.inboundCalls.toLocaleString()}
              </p>
            </div>
            <div className="rounded-lg bg-sky-50 p-3">
              <p className="text-sm text-sky-600">Answered</p>
              <p className="text-2xl font-bold text-sky-700">
                {totals.answeredCalls.toLocaleString()}
              </p>
              <p className="text-[10px] text-sky-500 leading-tight">Inbound only, excl. callbacks</p>
            </div>
            <div className="rounded-lg bg-purple-50 p-3">
              <p className="text-sm text-purple-600">Outbound</p>
              <p className="text-2xl font-bold text-purple-700">
                {totals.outboundCalls.toLocaleString()}
              </p>
            </div>
            <div className="rounded-lg bg-fuchsia-50 p-3">
              <p className="text-sm text-fuchsia-600">Voicemails</p>
              <p className="text-2xl font-bold text-fuchsia-700">
                {totalVoicemails.toLocaleString()}
              </p>
              <p className="text-[10px] text-fuchsia-500 leading-tight">Inbound only, page total</p>
            </div>
            <div className="rounded-lg bg-yellow-50 p-3">
              <p className="text-sm text-yellow-600"># of Holds</p>
              <p className="text-2xl font-bold text-yellow-700">
                {totals.totalHolds.toLocaleString()}
              </p>
            </div>
            <div className="rounded-lg bg-orange-50 p-3">
              <p className="text-sm text-orange-600">Avg Hold (min)</p>
              <p className="text-2xl font-bold text-orange-700">
                {fmt(totals.avgHoldMinutes)}
              </p>
              <p className="text-[10px] text-orange-500 leading-tight">In &amp; out</p>
            </div>
            <div className="rounded-lg bg-amber-50 p-3">
              <p className="text-sm text-amber-600">Avg ACW</p>
              <p className="text-2xl font-bold text-amber-700">
                {fmtAcw(totals.avgAcwSeconds)}
              </p>
              <p className="text-[10px] text-amber-500 leading-tight">In &amp; out</p>
            </div>
            <div className="rounded-lg bg-teal-50 p-3">
              <p className="text-sm text-teal-600">ASA</p>
              <p className="text-2xl font-bold text-teal-700">
                {fmtAsa(totals.avgAsaSeconds)}
              </p>
              <p className="text-[10px] text-teal-500 leading-tight">Inbound only</p>
            </div>
            <div className="rounded-lg bg-green-50 p-3">
              <p className="text-sm text-green-600">ATT (min/hr)</p>
              <p className="text-2xl font-bold text-green-700">
                {totals.attMinutes !== null ? fmt(totals.attMinutes) : "N/A"}
              </p>
              <p className="text-[10px] text-green-500 leading-tight">In &amp; out</p>
            </div>
            <div className="rounded-lg bg-emerald-50 p-3">
              <p className="text-sm text-emerald-600">AHT (min)</p>
              <p className="text-2xl font-bold text-emerald-700">
                {fmt(totals.ahtMinutes)}
              </p>
              <p className="text-[10px] text-emerald-500 leading-tight">In &amp; out</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading PCC performance...</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && filteredAgents.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <p className="text-gray-500">No call data found for the selected date range.</p>
        </div>
      )}

      {/* Table */}
      {!loading && filteredAgents.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th onClick={() => handlePCCSort("agent")} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Agent (PCC) {pccSortCol === "agent" ? (pccSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Position
                  </th>
                  <th onClick={() => handlePCCSort("inbound")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Inbound Calls {pccSortCol === "inbound" ? (pccSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handlePCCSort("outbound")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Outbound {pccSortCol === "outbound" ? (pccSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handlePCCSort("holds")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    # of Holds {pccSortCol === "holds" ? (pccSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handlePCCSort("avgHold")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Avg Hold (min) {pccSortCol === "avgHold" ? (pccSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handlePCCSort("avgAcw")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Avg ACW {pccSortCol === "avgAcw" ? (pccSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handlePCCSort("totalTalk")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Total Talk (min) {pccSortCol === "totalTalk" ? (pccSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handlePCCSort("schedHrs")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    Sched. Hrs {pccSortCol === "schedHrs" ? (pccSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handlePCCSort("att")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    ATT (min/hr) {pccSortCol === "att" ? (pccSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handlePCCSort("aht")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    AHT (min) {pccSortCol === "aht" ? (pccSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                  <th onClick={() => handlePCCSort("asa")} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer select-none hover:bg-gray-100">
                    ASA {pccSortCol === "asa" ? (pccSortDir === "asc" ? "↑" : "↓") : "↕"}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedFilteredAgents.map((agent) => (
                  <tr key={agent.agentId || agent.agentName} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">
                        {agent.agentName || agent.agentId || "Unknown"}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-left text-gray-600">
                      {agent.position ? fmtRole(agent.position) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-blue-600">
                      {agent.metrics.inboundCalls.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-purple-600">
                      {agent.metrics.outboundCalls.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-yellow-600">
                      {agent.metrics.totalHolds.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-orange-600">
                      {fmt(agent.metrics.avgHoldMinutes)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-amber-600">
                      {fmtAcw(agent.metrics.avgAcwSeconds)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-blue-600">
                      {fmt(agent.metrics.totalTalkMinutes)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-600">
                      {agent.metrics.scheduledHours !== null ? fmt(agent.metrics.scheduledHours) : <span className="text-gray-400">N/A</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-green-600">
                      {agent.metrics.attMinutes !== null ? fmt(agent.metrics.attMinutes) : <span className="text-gray-400">N/A</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-emerald-600">
                      {fmt(agent.metrics.ahtMinutes)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-teal-600">
                      {fmtAsa(agent.metrics.avgAsaSeconds)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Totals footer row */}
              {totals && (
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-4 py-3 font-bold text-gray-900">TOTAL</td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3 text-right font-bold text-blue-700">
                      {totals.inboundCalls.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-purple-700">
                      {totals.outboundCalls.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-yellow-700">
                      {totals.totalHolds.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-orange-700">
                      {fmt(totals.avgHoldMinutes)}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-amber-700">
                      {fmtAcw(totals.avgAcwSeconds)}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-blue-700">
                      {fmt(totals.totalTalkMinutes)}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-700">
                      {totals.scheduledHours !== null ? fmt(totals.scheduledHours) : "N/A"}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-green-700">
                      {totals.attMinutes !== null ? fmt(totals.attMinutes) : "N/A"}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-700">
                      {fmt(totals.ahtMinutes)}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-teal-700">
                      {fmtAsa(totals.avgAsaSeconds)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
